/**
 * Round-trip an OptimizedResumeDocument through a markdown-shaped text format so
 * a human can make light edits in a textarea.
 *
 * This is NOT a markdown parser. It reads back exactly one grammar — the one
 * `toMarkdown` writes — built from four primitives:
 *
 *   `# text`         document title (the candidate's name)
 *   `## Section`     top-level section
 *   `### Item`       one entry inside a section
 *   `**Key:** value` a scalar field on the enclosing block
 *   `- text`         a bullet on the enclosing block
 *   anything else    a paragraph on the enclosing block
 *
 * Every section uses those same primitives, so the parser is two small readers
 * plus one mapper per section. Dates and multi-value fields never rely on
 * positional splitting: a field the user deletes simply disappears rather than
 * shifting the meaning of its neighbours.
 *
 * `company` is deliberately absent from the text — it names the target employer,
 * not resume content — so `fromMarkdown` takes the original doc to carry it over.
 */

import { optimizedResumeDocument, type OptimizedResumeDocument } from "./schema";

/** Separator between repeated inline values (links, skill items use commas). */
const SEP = " · ";

export class ResumeMarkdownError extends Error {
  constructor(
    message: string,
    /** 1-based line number in the markdown, when the failure points at one. */
    readonly line?: number,
  ) {
    super(message);
    this.name = "ResumeMarkdownError";
  }
}

export type ParseResult = {
  doc: OptimizedResumeDocument;
  /** Recoverable oddities: unknown sections, dropped empties, guessed links. */
  warnings: string[];
};

// ---------------------------------------------------------------------------
// serialize
// ---------------------------------------------------------------------------

type Link = { label: string; url: string };

const writeLinks = (links: Link[]) =>
  links.map((l) => `[${l.label}](${l.url})`).join(SEP);

/** Emit `**Key:** value`, skipping null/empty so the reader never sees a blank. */
function kv(out: string[], key: string, value: string | null | undefined) {
  if (value == null || value.trim() === "") return;
  out.push(`**${key}:** ${value.trim()}`);
}

function writeDates(out: string[], start: string | null, end: string | null) {
  if (!start && !end) return;
  out.push(`**Dates:** ${start ?? "?"} – ${end ?? "Present"}`);
}

export function toMarkdown(doc: OptimizedResumeDocument): string {
  const out: string[] = [];

  out.push(`# ${doc.personal.name}`);
  kv(out, "Headline", doc.headline);
  kv(out, "Email", doc.personal.email);
  kv(out, "Phone", doc.personal.phone);
  kv(out, "Location", doc.personal.location);
  if (doc.personal.links.length) kv(out, "Links", writeLinks(doc.personal.links));

  if (doc.summary.length) {
    out.push("", "## Summary");
    for (const s of doc.summary) out.push(`- ${s}`);
  }

  if (doc.skills.length) {
    out.push("", "## Skills");
    for (const g of doc.skills) kv(out, g.category, g.items.join(", "));
  }

  if (doc.experience.length) {
    out.push("", "## Experience");
    for (const e of doc.experience) {
      out.push("", `### ${e.title} @ ${e.company}`);
      kv(out, "Location", e.location);
      writeDates(out, e.date_start, e.date_end);
      for (const b of e.bullets) out.push(`- ${b}`);
    }
  }

  if (doc.projects.length) {
    out.push("", "## Projects");
    for (const p of doc.projects) {
      out.push("", `### ${p.name}`);
      kv(out, "URL", p.url);
      if (p.links.length) kv(out, "Links", writeLinks(p.links));
      if (p.description) out.push(p.description);
      for (const b of p.bullets) out.push(`- ${b}`);
    }
  }

  if (doc.education.length) {
    out.push("", "## Education");
    for (const ed of doc.education) {
      out.push("", `### ${ed.institution}`);
      kv(out, "Degree", ed.degree);
      kv(out, "Field", ed.field);
      kv(out, "Location", ed.location);
      kv(out, "Date", ed.date);
      for (const d of ed.details) out.push(`- ${d}`);
    }
  }

  if (doc.certifications.length) {
    out.push("", "## Certifications");
    for (const c of doc.certifications) {
      out.push("", `### ${c.name}`);
      kv(out, "Issuer", c.issuer);
      kv(out, "Date", c.date);
    }
  }

  for (const cs of doc.custom_sections) {
    out.push("", `## Custom: ${cs.heading}`);
    for (const p of cs.paragraphs) out.push(p);
    for (const b of cs.bullets) out.push(`- ${b}`);
  }

  return out.join("\n") + "\n";
}

// ---------------------------------------------------------------------------
// parse
// ---------------------------------------------------------------------------

/**
 * One heading and the primitives directly under it. `keys` preserves insertion
 * order because the Skills section uses arbitrary keys as its categories.
 */
type Block = {
  heading: string;
  line: number;
  keys: Map<string, string>;
  bullets: string[];
  paragraphs: string[];
};

type Section = Block & { items: Block[] };

const emptyBlock = (heading: string, line: number): Block => ({
  heading,
  line,
  keys: new Map(),
  bullets: [],
  paragraphs: [],
});

const KEY_RE = /^\*\*\s*(.+?)\s*:\s*\*\*\s*(.*)$/;
const LINK_RE = /\[([^\]]*)\]\(([^)]*)\)/;
const blank = (s: string | null | undefined) => (s == null || s === "" ? null : s);

/** Split into the preamble block plus one Section per `##`. */
function lex(md: string): { preamble: Block; sections: Section[] } {
  const lines = md.split(/\r?\n/);
  const preamble = emptyBlock("", 1);
  const sections: Section[] = [];
  let seenTitle = false;
  // Where `**Key:**`, `- `, and paragraphs currently land.
  let target: Block = preamble;

  lines.forEach((raw, i) => {
    const line = raw.trim();
    const no = i + 1;
    if (!line) return;

    if (line.startsWith("# ")) {
      if (seenTitle) throw new ResumeMarkdownError("More than one `# Name` heading.", no);
      preamble.heading = line.slice(2).trim();
      seenTitle = true;
      target = preamble;
      return;
    }
    if (line.startsWith("## ")) {
      const s: Section = { ...emptyBlock(line.slice(3).trim(), no), items: [] };
      sections.push(s);
      target = s;
      return;
    }
    if (line.startsWith("### ")) {
      const s = sections[sections.length - 1];
      if (!s) throw new ResumeMarkdownError("`###` entry before any `##` section.", no);
      const item = emptyBlock(line.slice(4).trim(), no);
      s.items.push(item);
      target = item;
      return;
    }

    const kvMatch = KEY_RE.exec(line);
    if (kvMatch) {
      target.keys.set(kvMatch[1]!, kvMatch[2]!.trim());
      return;
    }
    if (line.startsWith("- ")) {
      target.bullets.push(line.slice(2).trim());
      return;
    }
    target.paragraphs.push(line);
  });

  if (!seenTitle) {
    throw new ResumeMarkdownError("Missing the `# Your Name` line at the top.", 1);
  }
  return { preamble, sections };
}

function readLinks(value: string, warnings: string[], where: string): Link[] {
  return value
    .split(SEP.trim())
    .map((c) => c.trim())
    .filter(Boolean)
    .map((chunk) => {
      const m = LINK_RE.exec(chunk);
      if (m) return { label: m[1]!.trim(), url: m[2]!.trim() };
      warnings.push(`${where}: "${chunk}" is not a [label](url) link — kept as-is.`);
      return { label: chunk, url: chunk };
    });
}

/**
 * Split on a dash that has whitespace around it, so `2020-01` stays intact.
 *
 * `?` is the writer's marker for a side that has no value; everything else comes
 * back verbatim, including "Present". A null end date is written as "Present"
 * rather than "?" because that is what a reader expects to see, and it reads back
 * as the literal string — which templates render identically to null
 * (`toTypstDate` maps both to today). So the first round-trip may turn a null end
 * into "Present", and every round-trip after that changes nothing.
 */
function readDates(value: string): [string | null, string | null] {
  const parts = value.split(/\s+[–—-]\s+/);
  const start = blank(parts[0]?.trim());
  const end = blank(parts.slice(1).join(" - ").trim());
  return [start === "?" ? null : start, end === "?" ? null : end];
}

/** Section name → its parser, matched case-insensitively. */
const KNOWN = ["summary", "skills", "experience", "projects", "education", "certifications"];

export function fromMarkdown(md: string, original: OptimizedResumeDocument): ParseResult {
  const warnings: string[] = [];
  const { preamble, sections } = lex(md);

  const get = (b: Block, key: string) => {
    for (const [k, v] of b.keys) if (k.toLowerCase() === key) return blank(v);
    return null;
  };

  const draft: OptimizedResumeDocument = {
    personal: {
      name: preamble.heading,
      email: get(preamble, "email"),
      phone: get(preamble, "phone"),
      location: get(preamble, "location"),
      links: readLinks(get(preamble, "links") ?? "", warnings, "Links"),
    },
    company: original.company,
    headline: get(preamble, "headline"),
    summary: [],
    skills: [],
    experience: [],
    projects: [],
    education: [],
    certifications: [],
    custom_sections: [],
  };

  if (!draft.personal.name) {
    throw new ResumeMarkdownError("The `#` line has no name after it.", preamble.line);
  }

  for (const s of sections) {
    const name = s.heading.toLowerCase();

    if (name.startsWith("custom:")) {
      const heading = s.heading.slice("custom:".length).trim();
      if (!heading) {
        warnings.push(`Line ${s.line}: a "## Custom:" section has no title — dropped.`);
        continue;
      }
      draft.custom_sections.push({
        heading,
        paragraphs: s.paragraphs,
        bullets: s.bullets,
      });
      continue;
    }

    switch (name) {
      case "summary":
        draft.summary = [...s.bullets, ...s.paragraphs];
        break;

      case "skills":
        for (const [category, value] of s.keys) {
          const items = value
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
          if (!items.length) {
            warnings.push(`Skills: "${category}" has no items — dropped.`);
            continue;
          }
          draft.skills.push({ category, items });
        }
        break;

      case "experience":
        for (const it of s.items) {
          const [title, company] = it.heading.split(/\s+@\s+/);
          if (!company) {
            warnings.push(
              `Line ${it.line}: "${it.heading}" is missing " @ Company" — company left blank.`,
            );
          }
          const [date_start, date_end] = readDates(get(it, "dates") ?? "");
          draft.experience.push({
            title: title?.trim() ?? it.heading,
            company: company?.trim() ?? "",
            location: get(it, "location"),
            date_start,
            date_end,
            bullets: it.bullets,
          });
        }
        break;

      case "projects":
        for (const it of s.items) {
          draft.projects.push({
            name: it.heading,
            url: get(it, "url"),
            links: readLinks(get(it, "links") ?? "", warnings, `Project "${it.heading}"`),
            description: blank(it.paragraphs.join(" ")),
            bullets: it.bullets,
          });
        }
        break;

      case "education":
        for (const it of s.items) {
          draft.education.push({
            institution: it.heading,
            degree: get(it, "degree"),
            field: get(it, "field"),
            location: get(it, "location"),
            date: get(it, "date"),
            details: it.bullets,
          });
        }
        break;

      case "certifications":
        for (const it of s.items) {
          draft.certifications.push({
            name: it.heading,
            issuer: get(it, "issuer"),
            date: get(it, "date"),
          });
        }
        break;

      default:
        warnings.push(
          `Line ${s.line}: "## ${s.heading}" is not a resume section — its content was dropped. ` +
            `Known sections: ${KNOWN.join(", ")}, or "Custom: Your Heading".`,
        );
    }
  }

  const parsed = optimizedResumeDocument.safeParse(draft);
  if (!parsed.success) {
    const issue = parsed.error.issues[0]!;
    const where = issue.path.join(".");
    throw new ResumeMarkdownError(where ? `${where}: ${issue.message}` : issue.message);
  }
  // The schema clamps an over-long list instead of rejecting it, because the
  // other consumer is a streamed model response where a rejection throws away
  // the whole rewrite. Here the author is a person, and a line of theirs just
  // disappeared — so say which one.
  warnings.push(...clampWarnings(draft, parsed.data));
  return { doc: parsed.data, warnings };
}

/** Where `capped()` in the schema dropped entries, reported per field. */
function clampWarnings(before: unknown, after: unknown, path = ""): string[] {
  if (Array.isArray(before) && Array.isArray(after)) {
    const dropped =
      before.length > after.length
        ? [
            `"${path}" holds ${before.length} entries but the resume allows ${after.length} — ` +
              `the last ${before.length - after.length} were dropped.`,
          ]
        : [];
    return dropped.concat(
      after.flatMap((item, i) => clampWarnings(before[i], item, `${path}[${i}]`)),
    );
  }
  if (before && after && typeof before === "object" && typeof after === "object") {
    return Object.keys(after as object).flatMap((key) =>
      clampWarnings(
        (before as Record<string, unknown>)[key],
        (after as Record<string, unknown>)[key],
        path ? `${path}.${key}` : key,
      ),
    );
  }
  return [];
}
